import { css } from "@emotion/react"
import styled from "@emotion/styled"
import React from "react"
import bulletSrc from "../images/bullet.svg"
import Theme from "../lib/Theme"
import { CloudinaryImage } from "./CloudinaryImage"
import { Heading } from "./Heading"
import { Text } from "./Text"

interface Props {
  first: boolean
}

export const Block = (props: Props & Kirby.Block) => {
  switch (props.type) {
    case "heading":
      const SIZES = {
        h1: 40,
        h2: 30,
      }

      const size = SIZES[props.level] || 20

      return (
        <HeadingBlock size={size} as={props.level}>
          {props.text}
        </HeadingBlock>
      )

    case "image":
      return (
        <Image>
          <CloudinaryImage
            publicId={props.src}
            alt={props.alt}
            width={1000}
            crop="scale"
          />
          {props.caption && <figcaption>{props.caption}</figcaption>}
        </Image>
      )

    case "quote":
      return (
        <Quote>
          <blockquote>
            <p>{props.text}</p>
          </blockquote>
          <figcaption>{props.citation}</figcaption>
        </Quote>
      )

    case "gallery":
      return (
        <Gallery>
          {props.images.map((image) => (
            <CloudinaryImage
              key={image.src}
              publicId={image.src}
              alt={image.alt}
              height={400}
              gravity="faces"
            />
          ))}
        </Gallery>
      )

    case "text":
    case "list":
      return (
        <TextWrap
          as="div"
          big={props.first}
          dangerouslySetInnerHTML={{ __html: props.text }}
        />
      )

    default:
      console.warn("Unhandled block", props)
      return null
  }
}

const HeadingBlock = styled(Heading)`
  margin-bottom: 0.25rem;
  line-height: 1.4;
`

const Image = styled.figure`
  margin: 1rem -1rem;

  ${Theme.Mediaquery.medium.min} {
    margin: 2rem -4rem;
  }

  img {
    display: block;
    max-width: 100%;
  }

  figcaption {
    text-align: center;
    color: ${Theme.Colors.grey.s50};
  }
`

const Gallery = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  margin: 0 -1rem;
  max-width: 100%;

  img {
    display: block;
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  ${Theme.Mediaquery.medium.min} {
    margin: 2rem -4rem;
  }
`

const TextWrap = styled(Text)<{ big: boolean }>`
  margin-bottom: 2rem;
  font-size: 1.1rem;

  ${Theme.Mediaquery.medium.min} {
    font-size: 1.2rem;
  }

  p:first-of-type {
    margin-top: 0;
  }

  ul,
  ol {
    list-style: url(${bulletSrc});
  }

  li p {
    margin-bottom: 0.25rem;
  }

  > p {
    max-width: 50rem;
    line-height: 1.7;
  }

  a {
    color: ${Theme.Colors.pink.s100};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
      color: ${Theme.Colors.purple.s100};
    }
  }

  ${(p) =>
    p.big &&
    css`
      font-size: 1.2rem;
      line-height: 1.3;
      margin-bottom: 3rem;
      font-weight: ${Theme.Font.body.bold};

      ${Theme.Mediaquery.medium.min} {
        font-size: 1.3rem;
      }
    `}

  & + & {
    margin-top: -1rem;
  }
`

const Quote = styled.figure`
  margin: 4rem 0;
  color: ${Theme.Colors.purple.s100};
  text-align: center;
  display: none;

  blockquote {
    font-size: 4rem;
    line-height: 1;
    margin: 0;

    p {
      margin: 0;
      font-family: "Parisienne";

      &:before,
      &:after {
        content: '"';
      }
    }
  }

  figcaption {
    margin: 0;

    &:before {
      content: "- ";
    }
  }
`
